import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Observable, Subject } from 'rxjs';
import { finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { Scope } from '../scope';
import { Oauth2ScopesService } from '../scopes-service';

@Component({
  selector: 'app-edit-scope',
  templateUrl: './edit-oauth2-scope.component.html',
  styleUrls: ['./edit-oauth2-scope.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class EditOauth2ScopeComponent implements OnInit, OnDestroy {
  private _destroyed$$: Subject<void> = new Subject<void>();

  public readonly id$: Observable<string> = this.route.params.pipe(map((r) => r.id));
  public readonly scope$: Observable<Scope> = this.id$.pipe(
    switchMap((id: string) => this.oauth2ScopesService.getScope(id)),
  );
  public form: UntypedFormGroup;
  public isDisabled = false;
  public loading = true;

  constructor(
    private oauth2ScopesService: Oauth2ScopesService,
    private route: ActivatedRoute,
    private router: Router,
    private alertService: SnackbarService,
  ) {}

  ngOnInit(): void {
    this.scope$.pipe(takeUntil(this._destroyed$$)).subscribe((scope: Scope) => {
      this.form = new UntypedFormGroup({
        id: new UntypedFormControl({ value: scope.id, disabled: true }, Validators.required),
        name: new UntypedFormControl(scope.name, Validators.required),
        description: new UntypedFormControl(scope.description, Validators.required),
      });
      this.loading = false;
    });
  }

  onSubmit(): void {
    if (!this.form.valid) {
      this.alertService.openErrorSnack('Form invalid, make sure you have filled out the required fields.');
      return;
    }
    this.isDisabled = true;
    // "created: null" here is just to satisfy the interface, we're not actually modifying created to be null
    this.oauth2ScopesService
      .updateScope({ id: this.id, name: this.name, description: this.description, created: null })
      .pipe(
        finalize(() => {
          this.isDisabled = false;
        }),
      )
      .subscribe(
        () => {
          this.alertService.openSuccessSnack('Scope updated');
          this.goBack();
        },
        (err: HttpErrorResponse) => this.alertService.openErrorSnack('Error updating scope: ' + err.error.message),
      );
  }

  onCancel(): void {
    this.goBack();
  }

  goBack(): void {
    this.router.navigate(['../../'], { relativeTo: this.route });
  }

  get id(): string {
    return this.form.get('id').value;
  }

  get name(): string {
    return this.form.get('name').value;
  }

  get description(): string {
    return this.form.get('description').value;
  }

  ngOnDestroy(): void {
    this._destroyed$$.next(null);
    this._destroyed$$.complete();
  }
}
